import React from 'react';
import UnitIcon from '@components/game/objects/UnitIcon';
import EffectIcon from '@components/ui/icons/EffectIcon';
import HoverClickPopup from '@components/ui/general/HoverClickPopup';
import CooldownBlocks from '@components/ui/game/cooldown-blocks';
import { formatStatWithModifier, getModifierBreakdown } from "@utils/statCalculations";
import { getSourceIcon } from "@utils/statIcons";
import useGameStore from "@store/useGameStore";
import "./StatDisplay.css";

/**
 * Component to display a stat with modifiers and detailed tooltip
 */
export default function StatDisplay({ unit, statName, icon, tooltip }) {
  const statInfo = formatStatWithModifier(unit, statName);
  const breakdown = getModifierBreakdown(unit, statName);

  // Determine modifier class (positive/negative/neutral)
  const getModifierClass = () => {
    if (!statInfo.hasModifier) return '';
    if (statInfo.modifier > 0) return 'positive';
    if (statInfo.modifier < 0) return 'negative';
    return 'neutral';
  };
  const modifierClass = getModifierClass();

  // Build the stat value display
  const renderStatValue = () => {
    if (!statInfo.hasModifier) {
      return <span className="stat-value">{statInfo.text}</span>;
    }

    return (
      <span className={`stat-value modified ${modifierClass}`}>
        {statInfo.effective}
      </span>
    );
  };

  // Check if stat has modifiers (for popup content)
  const hasModifier = statInfo.hasModifier;

  // Icon without color modifications - use currentColor (white)
  const styledIcon = icon;
  
  // Trigger element for the popup
  const triggerElement = (
    <span
      className={`stat-display stat-display-${statName} ${modifierClass}`}
      style={{ cursor: 'pointer' }}
    >
      {styledIcon}
      {renderStatValue()}
    </span>
  );
  
  // Popup content
  const popupContent = (
    <>
      {/* Header with icon and title */}
      <div className="click-popup-header">
        <div className="click-popup-title-group">
          <span className="click-popup-icon">{styledIcon}</span>
          <span className="click-popup-title">
            {statName.charAt(0).toUpperCase() + statName.slice(1)}
          </span>
        </div>
      </div>

      {/* Description */}
      <div className="click-popup-description">
        {tooltip}
      </div>

      {/* Modifiers section - only show if stat has modifiers */}
      {statInfo.hasModifier && (
        <>
          <div className="click-popup-separator" />
          <div className="click-popup-modifiers">
            {/* Base value */}
            <div className="click-popup-base-row">
              <span>Base</span>
              <span className="stat-value">{breakdown.base}</span>
            </div>

            {/* Modifiers list */}
            {breakdown.modifiers.length > 0 && (
              <>
                <div className="click-popup-modifier-separator" />
                <div className="click-popup-modifiers-section">
                  {breakdown.modifiers.map((mod, index) => {
                    const isPositive = mod.value > 0;
                    const sign = isPositive ? '+' : '';
                    const rowClass = isPositive ? 'positive' : 'negative';

                    // Render icon based on source type
                    const renderSourceIcon = () => {
                      // Check if source is a unit with valid ID
                      if (mod.source?.type === 'unit' && mod.source?.id) {
                        // Validate the ID is a number
                        const unitId = parseInt(mod.source.id);
                        if (!isNaN(unitId) && unitId > 0) {
                          // Fetch the unit object using the ID
                          const sourceUnit = useGameStore.getState().getUnitOrAvailableUnitById(unitId);
                          if (sourceUnit) {
                            return (
                              <span className="modifier-icon" style={{ pointerEvents: 'none' }}>
                                <UnitIcon
                                  unitId={sourceUnit.id}
                                  size="small"
                                />
                              </span>
                            );
                          } else {
                            console.warn('[StatDisplay] Could not find unit for ID:', unitId);
                          }
                        } else {
                          console.warn('[StatDisplay] Invalid unit ID in source:', mod.source);
                        }
                      }
                      return <span className="modifier-icon">{getSourceIcon(mod.source)}</span>;
                    };

                    return (
                      <div key={`mod-${index}`} className={`click-popup-modifier-row ${rowClass}`}>
                        {renderSourceIcon()}
                        <span className="modifier-name">
                          <EffectIcon
                            effectId={mod.effectId}
                            variant="with-name"
                            className={`effect-modifier ${rowClass}`}
                          />
                        </span>
                        <span className="modifier-value">{sign}{mod.value}</span>
                        <span className="modifier-duration">
                          {mod.unlimited ? (
                            mod.counter_state
                              ? `∞ • ${mod.counter_state.current_turn_triggers || 0}/${mod.counter_state.max_per_turn || 0}`
                              : '∞'
                          ) : (
                            <CooldownBlocks
                              cooldownMax={mod.max_on_turn_start_counters}
                              cooldownCurrent={mod.on_turn_start_counters}
                              mode="duration"
                            />
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Total/Effective value */}
                <div className="click-popup-modifier-separator" />
                <div className="click-popup-total-row">
                  <span>Effective</span>
                  <span className="stat-value-final">{breakdown.effective}</span>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
  
  return (
    <HoverClickPopup trigger={triggerElement}>
      {popupContent}
    </HoverClickPopup>
  );
}